import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MdEmail, MdBusiness, MdLocationOn, MdPerson } from "react-icons/md";
import { RiLockPasswordLine, RiPhoneLine } from "react-icons/ri";
import { appPrimaryColor, responsiveViewToast, US_STATES_LIST } from "../utils/constants";
import { toast } from "react-toastify";
import InnerLoader from "../components/microComponents/InnerLoader";
import { registerFarmer, registerOrg, getInviteDetails } from "../api";
import { setUser } from "../utils";
import { auth } from "../firebase/firebaseConfig";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { InputField, SelectField } from "../components/form";
import AddressSearchField from "../components/form/AddressSearchField";

const setLocalUser = setUser;

/**
 * The component is used to sign up a farmer and organization
 * TODO:
 * 1. Register farmer on Firebase
 * 2. Register farmer on the backend
 * 3. Save user details in local storage
 * 4. Navigate to login page
 */

const SignUpFarmer = ({ isFarmer }) => {
  const navigate = useNavigate();
  const { token } = useParams();
  // const signUpOrgBoolean = isFarmer;
  // console.log("🚀 ~ SignUpFarmer ~ token:", signUpOrgBoolean)
  const [user, setUser] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    first_name: "",
    last_name: "",
    farm_name: "",
    farm_address: "",
    org_name: "",
    org_address: "",
    zipcode: "",
    state: "",
    city: "",
    phone: "",
    showPassword: false,
    showConfirmPassword: false,
  });

  useEffect(() => {
    getInviteDetails(token.split(":")[1]).then((data) => {
      // console.log("🚀 ~ SignUpFarmer ~ email:", data?.email)
      setUser(prev => ({
        ...prev,
        email: data?.email
      }))
    }).catch((error) => {
      console.error("Error getting invite details:", error);
    })
  }, [token])


  const handleInputChange = (field, value, validator = null) => {
    if (validator && !validator(value)) {
      return;
    }
    setUser(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const [disableButton, setDisableButton] = useState(false);
  const [error, setError] = useState("");

  const handleSignUp = async (e) => {

    e.preventDefault();
    setDisableButton(true)
    // check email
    // if (!user.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/u)) {
    //   toast.error("Please enter a valid email address.", responsiveViewToast(window.innerWidth));
    //   setDisableButton(false)
    //   return;
    // }

    // Check if the password is at least 6 characters long
    if (user.password.length < 8) {
      toast.error("Password must be at least 6 characters long.", responsiveViewToast(window.innerWidth));
      setDisableButton(false)
      return;
    }

    // Check if passwords match
    if (user.password !== user.confirmPassword) {
      setError("Passwords do not match. Please try again.", responsiveViewToast(window.innerWidth));
      setDisableButton(false)
      return;
    }

    // check phone number is valid
    if (!user.phone.match(/^^$|(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/u)) {
      toast.error("Please enter a valid phone number. Example: 1234567890", responsiveViewToast(window.innerWidth));
      setDisableButton(false)
      return;
    }

    // Check if there is no address
    if (isFarmer && !user.farm_address) {
      toast.error("Please enter your farm address.", responsiveViewToast(window.innerWidth));
      setDisableButton(false)
      return;
    }

    if (!isFarmer && !user.org_address) {
      toast.error("Please enter your organization address.", responsiveViewToast(window.innerWidth));
      setDisableButton(false)
      return;
    }

    // check state is valid
    if (!user.state) {
      toast.error("Please select a state.", responsiveViewToast(window.innerWidth));
      setDisableButton(false)
      return;
    }

    // check city is valid
    if (!user.city) {
      toast.error("Please enter your city.", responsiveViewToast(window.innerWidth));
      setDisableButton(false)
      return;
    }

    // check zipcode is valid
    if (!user.zipcode.match(/^(^$|[0-9]{5}(?:-[0-9]{4})?)$/u)) {
      toast.error("Please enter a valid zipcode.", responsiveViewToast(window.innerWidth));
      setDisableButton(false)
      return;
    }

    const data = {
      email: user.email,
      first_name: user.first_name?.trim(),
      last_name: user.last_name?.trim(),
      zipcode: user.zipcode?.trim(),
      phone: user.phone?.trim(),
      invite_id: token.split(":")[1],
      state: user.state,
      city: user.city,
      ...(isFarmer
        ? {
          farm_name: user.farm_name?.trim(),
          farm_address: user.farm_address?.trim(),
          invite_type: 'farmer'
        }
        : {
          org_name: user.org_name?.trim(),
          address: `${user.org_address?.trim()}, ${user.city?.trim()}, ${user.state?.trim()}, USA, ${user.zipcode?.trim()}`,
          invite_type: 'org'
        }
      )
    };

    // 1. Register farmer on Firebase ------------
    createUserWithEmailAndPassword(auth, user.email, user.password)
      .then((responseTwo) => {
        // console.log("🚀 ~ .then ~ responseTwo:", responseTwo);
        if (responseTwo?.user?.accessToken) {
          // 2. Register farmer on the backend ------------
          const apiFunction = isFarmer ? registerFarmer : registerOrg;
          apiFunction(data, responseTwo?.user?.accessToken)
            .then((responseThree) => {
              if (responseThree.status === 201) {
                // TO DO: Save user details in local storage which we get from backend
                setLocalUser({
                  ...responseTwo?.user,
                  ...responseThree?.new_user,
                });
                console.log("Farmer registered successfully");
                setError("");
                toast.success("Sign-Up Successful!", responsiveViewToast(window.innerWidth));
                setTimeout(() => {
                  navigate("/login");
                  // reload the page
                  window.location.reload();
                }, 500);
              } else {
                console.error(
                  "Failed to register farmer:",
                  responseThree
                );
                setDisableButton(false)
                toast.error(
                  "Failed to register farmer. Please try again.", responsiveViewToast(window.innerWidth)
                );
              }
            })
            .catch((error) => {
              console.error("Error registering farmer:", error);
              toast.error("Failed to register farmer. Please try again.", responsiveViewToast(window.innerWidth));
              setDisableButton(false)
            });
        }
      })
      .catch((error) => {
        console.error("Error signing up:", error);
        toast.error("Failed to sign up. Please try again later.", responsiveViewToast(window.innerWidth));
        setDisableButton(false)
      });
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Left Side - Background */}
      <div className="hidden md:flex md:w-1/2 bg-darkGreen items-center justify-center p-12">
        <div className="max-w-md text-white">
          <h1 className="text-4xl font-bold mb-6">Welcome to Share Farm</h1>
          <p className="text-lg opacity-90">
            Complete your registration to join our farming community and start managing your agricultural data.
          </p>
        </div>
      </div>

      {/* Right Side - Sign Up Form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-gray-50">
        <div className="w-full max-w-2xl space-y-8 bg-white p-8 rounded-2xl shadow-lg">
          {/* Header */}
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">Complete Registration</h2>
            <p className="mt-2 text-sm text-gray-600">
              Fill in your details to create your {isFarmer ? "farmer" : "organization"} account
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSignUp} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <InputField
                  label="Email Address"
                  icon={MdEmail}
                  type="email"
                  name="email"
                  value={user.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="Enter your email"
                  disabled={true}
                />
              </div>


              {/* Add Password Fields */}
              <div className="md:col-span-2">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Password</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <RiLockPasswordLine className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type={user.showPassword ? "text" : "password"}
                      value={user.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      placeholder="Enter your password"
                      className="block w-full pl-10 pr-12 py-2.5 text-gray-900 rounded-lg border border-gray-300 
                         focus:ring-2 focus:ring-darkGreen focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={() => setUser(prev => ({ ...prev, showPassword: !prev.showPassword }))}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      <span className="text-sm text-gray-500 hover:text-gray-700">
                        {user.showPassword ? 'Hide' : 'Show'}
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              <div className="md:col-span-2">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Confirm Password</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <RiLockPasswordLine className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type={user.showConfirmPassword ? "text" : "password"}
                      value={user.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      placeholder="Confirm your password"
                      className="block w-full pl-10 pr-12 py-2.5 text-gray-900 rounded-lg border border-gray-300 
                         focus:ring-2 focus:ring-darkGreen focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={() => setUser(prev => ({ ...prev, showConfirmPassword: !prev.showConfirmPassword }))}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      <span className="text-sm text-gray-500 hover:text-gray-700">
                        {user.showConfirmPassword ? 'Hide' : 'Show'}
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              {isFarmer &&
                <InputField
                  label="First Name"
                  icon={MdPerson}
                  name="first_name"
                  value={user.first_name}
                  onChange={(e) => handleInputChange(
                    'first_name',
                    e.target.value,
                    value => !value || value.match(/^[A-Za-z\s]*$/)
                  )}
                  placeholder="Enter your first name"
                  pattern="[A-Za-z\s]+"
                />}

              {isFarmer &&
                <InputField
                  label="Last Name"
                  icon={MdPerson}
                  name="last_name"
                  value={user.last_name}
                  onChange={(e) => handleInputChange(
                    'last_name',
                    e.target.value,
                    value => !value || value.match(/^[A-Za-z\s]*$/)
                  )}
                  placeholder="Enter your last name"
                  pattern="[A-Za-z\s]+"
                />}

              {isFarmer ?
                <InputField
                  label="Farm Name"
                  icon={MdBusiness}
                  name="farm_name"
                  value={user.farm_name}
                  onChange={(e) => handleInputChange(
                    "farm_name",
                    e.target.value
                  )}
                  placeholder="Enter your farm name"
                /> :
                <InputField
                  label="Organization Name"
                  icon={MdBusiness}
                  name="org_name"
                  value={user.org_name}
                  onChange={(e) => handleInputChange(
                    "org_name",
                    e.target.value
                  )}
                  placeholder="Enter your organization name"
                />
              }

              <InputField
                label="Phone Number"
                icon={RiPhoneLine}
                type="tel"
                name="phone"
                value={user.phone}
                onChange={(e) => handleInputChange(
                  'phone',
                  e.target.value,
                  value => !value || (value.length <= 10 && /^\d*$/.test(value))
                )}
                placeholder="Enter your phone number"
                pattern="[0-9]{10}"
              />

              <div className="md:col-span-2">
                {isFarmer ? (
                  <AddressSearchField
                    label="Farm Address"
                    name="farm_address"
                    value={user.farm_address}
                    onChange={(e) => handleInputChange("farm_address", e.target.value)}
                    onChangeState={(state) => {
                      const stateAbbreviation = US_STATES_LIST.find(item => item.abbreviation === state)?.value;
                      if (stateAbbreviation) {
                        handleInputChange("state", stateAbbreviation)
                      } else {
                        const stateFullName = US_STATES_LIST.find(item => item.value === state)?.value;
                        if (stateFullName) {
                          handleInputChange("state", stateFullName)
                        }
                      }
                      handleInputChange("zipcode", "")
                    }}
                    onChangeCity={(city) => {
                      handleInputChange("city", city)
                    }}
                    onChangeZipcode={(zipcode) => {
                      handleInputChange("zipcode", zipcode)
                    }}
                    placeholder="Search for your farm address"
                  />
                ) : (
                  <AddressSearchField
                    label="Organization Address"
                    name="org_address"
                    value={user.org_address}
                    onChange={(e) => handleInputChange("org_address", e.target.value)}
                    onChangeState={(state) => {
                      const stateAbbreviation = US_STATES_LIST.find(item => item.abbreviation === state)?.value;
                      if (stateAbbreviation) {
                        handleInputChange("state", stateAbbreviation)
                      } else {
                        const stateFullName = US_STATES_LIST.find(item => item.value === state)?.value;
                        if (stateFullName) {
                          handleInputChange("state", stateFullName)
                        }
                      }
                      handleInputChange("zipcode", "")
                    }}
                    onChangeCity={(city) => {
                      handleInputChange("city", city)
                    }}
                    onChangeZipcode={(zipcode) => {
                      handleInputChange("zipcode", zipcode)
                    }}
                    placeholder="Search for your organization address"
                  />
                )}
              </div>

              {/* Add City Input Field */}
              <InputField
                label="City"
                icon={MdLocationOn}
                name="city"
                value={user.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                placeholder="Enter your city"
              />

              {/* US State Input Field */}
              <SelectField
                label="US State"
                icon={MdLocationOn}
                name="state"
                value={user.state}
                onChange={(e) => handleInputChange('state', e.target.value)}
                options={US_STATES_LIST}
                placeholder="Select a state"
              />

              {/* Zipcode Input Field */}
              <InputField
                label="Zipcode"
                icon={MdLocationOn}
                type="number"
                name="zipcode"
                maxLength={5}
                value={user.zipcode}
                onChange={(e) => handleInputChange('zipcode', e.target.value)}
                placeholder="Enter your zipcode"
              />

              {/* Add Country Input Field */}
              <InputField
                label="Country"
                icon={MdLocationOn}
                name="country"
                value="USA"
                disabled={true}
                className="bg-gray-100"
                placeholder="Country"
                editable={false}
              />
            </div>


            {error && (
              <div className="text-center">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={disableButton}
              className={`w-full flex justify-center items-center gap-2 py-3 px-4 rounded-lg text-white font-medium transition-all ${disableButton ? 'bg-gray-400 cursor-not-allowed' : `${appPrimaryColor} hover:opacity-90`}`}
            >
              {disableButton ? (
                <>
                  <InnerLoader
                    divSize={{ width: "20px", height: "20px" }}
                    loaderSize={{ width: "20px", height: "20px" }}
                  />
                  <span>Creating Account...</span>
                </>
              ) : (
                'Create Account'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SignUpFarmer;